import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader } from "lucide-react";
import api from "../../../../utils/api";
import { Dot, Mono, BatonTrack, fmtD, fmtDT, relDays } from "./ui";
import { JourneyTab, BomTab, TimelineTab } from "./PalletSections";
import HandoffPanel from "./HandoffPanel";
import PalletComments from "./PalletComments";

// The pallet window: where the pallet is, journey, BOM & checks, timeline, hand-off and comments
export default function PalletWindow({ palletId, reportDate, onClose, onChanged, toast }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("journey");
    const ref = useRef(null);
    const ry = reportDate ? Number(reportDate.slice(0, 4)) : undefined;

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/api/excel-ingestion/pallets/${encodeURIComponent(palletId)}`);
            setData(res.data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || "Could not load this pallet");
        }
    }, [palletId]);

    useEffect(() => { load(); }, [load]);

    // Escape closes; lock page scroll while open
    useEffect(() => {
        const k = e => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", k);
        ref.current?.focus();
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", k); document.body.style.overflow = prev; };
    }, [onClose]);

    // After a comment / hand-off: refresh this window and the board behind it
    const changed = () => { load(); onChanged(); };

    const p = data?.pallet;
    const c = p?.custody;
    const fails = p ? (p.crossChecks || []).filter(x => x.result === "fail").length : 0;
    const TABS = [["journey", "Journey & milestones"], ["bom", `BOM & checks${fails ? ` · ${fails} failed` : ""}`], ["timeline", `Timeline · ${data?.events?.length ?? 0}`]];

    return (
        <div className="fixed inset-0 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.80)", zIndex: 9999 }}
            onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="pw-title"
                className="bg-[#0B1120] border border-[#1F2937] rounded-2xl w-full max-w-7xl my-4 shadow-2xl outline-none">
                <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#1F2937]">
                    {p ? (
                        <div className="flex items-start gap-3 min-w-0">
                            <span className="mt-2"><Dot health={p.popup?.health}/></span>
                            <div className="min-w-0">
                                <h2 id="pw-title" className="text-lg font-bold text-white">{p.customer} · {p.program}</h2>
                                <div className="text-[11px] text-slate-500">
                                    <Mono>{p.id}</Mono> · {p.country} {p.plantCode} · {p.palletQty} pallets · {p.bom?.components} SKUs ·
                                    HDM {p.hdmCode ? <Mono>{p.hdmCode}</Mono> : "not created"}
                                    {p.refs?.stoNumber && <> · STO <Mono>{p.refs.stoNumber}</Mono></>}
                                </div>
                            </div>
                        </div>
                    ) : <h2 id="pw-title" className="text-lg font-bold text-white">Pallet</h2>}
                    <div className="flex items-start gap-4 shrink-0">
                        {p && (
                            <div className="text-right">
                                <div className="text-[10px] text-slate-500">DC date</div>
                                <b className="text-white">{fmtD(p.dcDate, ry)}</b>
                                <div className="text-[10px] text-slate-500">{relDays(p.dcDate, reportDate)}</div>
                            </div>
                        )}
                        <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center text-slate-400 hover:text-white">
                            <X size={15}/>
                        </button>
                    </div>
                </header>

                {error && <div className="m-6 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2 text-[12px] text-red-400">{error}</div>}
                {!data && !error && <div className="p-16 flex justify-center"><Loader size={20} className="animate-spin text-slate-600"/></div>}

                {p && (
                    <div className="p-6 space-y-5">
                        <section aria-label="Where the pallet is" className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-3">
                            <BatonTrack custody={c}/>
                            <p className="text-[12px] text-slate-300 border-l-2 border-blue-500/60 pl-3">
                                <b className="text-white">Sits with {c.sitsWith}</b> ({c.party}) · waiting for {c.waitingFor}
                                {c.due ? ` · due ${fmtD(c.due, ry)}` : ""}
                                {c.daysLate > 0 && <> · <strong className="text-red-400">{c.daysLate} days late</strong></>}.
                                {c.nextTeam && <> Next in line: <b className="text-white">{c.nextTeam}</b>.</>}
                                {c.source === "manual" && <span className="text-slate-500"> (Sent by {c.handedOffBy?.name || "a user"} {fmtDT(c.handedOffAt)})</span>}
                            </p>
                        </section>

                        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-5">
                            <div className="min-w-0 bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-4">
                                <div className="flex flex-wrap gap-1 border-b border-[#1F2937] pb-3" role="tablist">
                                    {TABS.map(([k, l]) => (
                                        <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                                            className={`px-3 py-1.5 rounded-lg text-[12px] ${tab === k ? "bg-[#1F2937] text-white" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
                                    ))}
                                </div>
                                <div role="tabpanel">
                                    {tab === "journey" && <JourneyTab p={p} ry={ry}/>}
                                    {tab === "bom" && <BomTab p={p} lines={data.bomLines} ry={ry}/>}
                                    {tab === "timeline" && <TimelineTab events={data.events} ry={ry}/>}
                                </div>
                            </div>
                            <aside className="space-y-5 min-w-0">
                                <HandoffPanel key={p.id + c.stage + c.sitsWith} p={p} stages={data.stages} teams={data.teams} contacts={data.contacts}
                                    outbox={data.outbox} ry={ry} onDone={changed} toast={toast}/>
                                <PalletComments p={p} lines={data.bomLines} comments={data.comments} ry={ry} onChanged={changed}/>
                            </aside>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
